import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetWhitePaperReport, useGetWhitePaperReportPreviousVersions } from '../api/report/report';
import { useGetClaimsForCurrentUser } from '../api/user/user';
import { appInsights } from '../ApplicationInsightsService';
import { useCurrentEnterprise } from '../contexts/useCurrentEnterprise';
import { IWhitePaperReport, IWhitePaperReportChanges } from '../model';
import { DB_VERSION } from '../utils/IndexedDBSchemaRegistry';
import {
    adaptWhitePaperReportToCacheRecord,
    DB_WHITEPAPERREPORT_NAME,
    getWhitePaperCacheStoreName,
    getWhitePaperReportRecordId,
    IWhitePaperReportCacheRecord,
    parseWhitePaperReportFromCache,
} from '../utils/WhitePaperIndexedDBUtils';
import { useIndexedDB } from './useIndexedDB';
import { useSelectedEntities } from './useSelectedEntities';
import { useTaxReturnCalculationState } from './useTaxReturnCalculationState';

export interface UseWhitePaperReportCacheResult {
    cachedWhitePaperReport: IWhitePaperReport | undefined;
    loadWhitePaperReportData: (
        currentVersion?: string,
        forceRefresh?: boolean,
        isFormsLoading?: boolean
    ) => Promise<IWhitePaperReport | null>;
    loadWhitePaperReportDataCoordinated: (isFormsLoading: boolean) => Promise<IWhitePaperReport | null>;
    updateCachedReportWithChanges: (
        currentReport: IWhitePaperReport,
        updatedChangeTrackingData: IWhitePaperReportChanges,
        reCalculationRequired: boolean,
        currentReportId: number,
        currentVersionId: string
    ) => Promise<IWhitePaperReport>;
    isLoading: boolean;
    availableVersions: any[] | undefined;
    latestCalculatedVersionId: string;
}

interface UseWhitePaperReportCacheOptions {
    hasCompletedCalculation?: boolean;
}

export const useWhitePaperReportCache = (reportId: number, options?: UseWhitePaperReportCacheOptions): UseWhitePaperReportCacheResult => {
    // Essential API hooks
    const { refetch: refetchGetWhitePaperReport, isFetching: isFetchingWhitePaperReport } = useGetWhitePaperReport<IWhitePaperReport>(
        { ReportId: reportId },
        { query: { enabled: false } }
    );
    const { data: availableVersions } = useGetWhitePaperReportPreviousVersions({ ReportId: reportId });

    const { data: userClaims } = useGetClaimsForCurrentUser();
    const { enterprise } = useCurrentEnterprise();
    const { selectedEntities } = useSelectedEntities();

    const { isReportExecuting } = useTaxReturnCalculationState();
    const isCurrentlyExecuting = isReportExecuting(reportId);

    const { insertRecord, getRecord, deleteRecord, removePreviousVersions } = useIndexedDB<IWhitePaperReportCacheRecord>(
        window.indexedDB,
        DB_VERSION
    );

    const [cachedWhitePaperReport, setCachedWhitePaperReport] = useState<IWhitePaperReport | undefined>(undefined);

    const storeName = useMemo(() => {
        return getWhitePaperCacheStoreName(userClaims) || '';
    }, [userClaims]);

    const latestCalculatedVersionId = useMemo(() => {
        if (!availableVersions || availableVersions.length === 0) {
            return 'no-version';
        }

        const versionId = availableVersions[0]?.versionId?.toString();
        if (!versionId) {
            return 'no-version';
        }

        return versionId;
    }, [availableVersions]);

    const hasLoadedRef = useRef<number | null>(null);
    const wasExecutingRef = useRef<boolean>(false);
    const shouldUseCalculationState = options !== undefined;
    const hasCompletedCalculation = options?.hasCompletedCalculation;
    const shouldWaitForCalculationState = shouldUseCalculationState && hasCompletedCalculation === undefined;

    const loadWhitePaperReportData = useCallback(
        async (currentVersion?: string, forceRefresh?: boolean, isFormsLoading?: boolean): Promise<IWhitePaperReport | null> => {
            const hasVersions = availableVersions && availableVersions.length > 0;
            const hasCurrentVersion = currentVersion !== undefined && currentVersion !== null;
            const isVersionOutdated = hasCurrentVersion && latestCalculatedVersionId > currentVersion;
            const canFetchReportWithoutVersion = forceRefresh || !shouldUseCalculationState || hasCompletedCalculation === true;
            const shouldFetchFromAPI = isVersionOutdated || (!hasVersions && canFetchReportWithoutVersion);
            const shouldSkipApiFetch = latestCalculatedVersionId === 'no-version' && !canFetchReportWithoutVersion;

            if (!forceRefresh) {
                if (storeName) {
                    const recordId = getWhitePaperReportRecordId(reportId.toString(), enterprise?.enterpriseId, latestCalculatedVersionId);
                    const cachedRecord = await getRecord(DB_WHITEPAPERREPORT_NAME, storeName, recordId);

                    if (cachedRecord) {
                        const parsedReport = parseWhitePaperReportFromCache(cachedRecord);
                        if (parsedReport) {
                            if (isFormsLoading) {
                                return null;
                            }
                            setCachedWhitePaperReport(parsedReport);
                            return parsedReport;
                        }
                    }
                }
            } else {
                if (storeName) {
                    const recordId = getWhitePaperReportRecordId(reportId.toString(), enterprise?.enterpriseId, latestCalculatedVersionId);
                    await deleteRecord(DB_WHITEPAPERREPORT_NAME, storeName, recordId);
                }
            }

            if (shouldSkipApiFetch) {
                return null;
            }

            if (forceRefresh || shouldFetchFromAPI) {
                try {
                    const response = await refetchGetWhitePaperReport();
                    if (response?.data) {
                        setCachedWhitePaperReport(response.data);
                        if (storeName) {
                            try {
                                await removePreviousVersions(DB_WHITEPAPERREPORT_NAME, storeName, latestCalculatedVersionId, {
                                    indexName: 'returnKey',
                                    query: reportId.toString(),
                                });

                                const cacheRecord = adaptWhitePaperReportToCacheRecord(
                                    reportId.toString(),
                                    response.data,
                                    enterprise?.enterpriseId,
                                    latestCalculatedVersionId
                                );

                                await insertRecord(DB_WHITEPAPERREPORT_NAME, storeName, cacheRecord);
                            } catch (cacheError) {
                                appInsights?.trackException({ exception: cacheError as Error });
                            }
                        }

                        return response.data;
                    }
                } catch (apiError) {
                    appInsights?.trackException({ exception: apiError as Error });
                }
                return null;
            }

            try {
                const response = await refetchGetWhitePaperReport();
                if (response?.data) {
                    setCachedWhitePaperReport(response.data);

                    if (storeName) {
                        try {
                            await removePreviousVersions(DB_WHITEPAPERREPORT_NAME, storeName, latestCalculatedVersionId, {
                                indexName: 'returnKey',
                                query: reportId.toString(),
                            });

                            const cacheRecord = adaptWhitePaperReportToCacheRecord(
                                reportId.toString(),
                                response.data,
                                enterprise?.enterpriseId,
                                latestCalculatedVersionId
                            );

                            await insertRecord(DB_WHITEPAPERREPORT_NAME, storeName, cacheRecord);
                        } catch (cacheError) {
                            appInsights?.trackException({ exception: cacheError as Error });
                        }
                    }

                    return response.data;
                }
            } catch (apiError) {
                appInsights?.trackException({ exception: apiError as Error });
            }

            return null;
        },
        [
            availableVersions,
            latestCalculatedVersionId,
            refetchGetWhitePaperReport,
            storeName,
            reportId,
            selectedEntities,
            getRecord,
            deleteRecord,
            removePreviousVersions,
            insertRecord,
            enterprise?.enterpriseId,
            shouldUseCalculationState,
            hasCompletedCalculation,
        ]
    );

    const updateCachedReportWithChanges = async (
        currentReport: IWhitePaperReport,
        updatedChangeTrackingData: IWhitePaperReportChanges,
        reCalculationRequired: boolean,
        currentReportId: number,
        currentVersionId: string
    ): Promise<IWhitePaperReport> => {
        try {
            const updatedReport: IWhitePaperReport = {
                ...currentReport,
                changeTrackingData: updatedChangeTrackingData,
                reCalculationRequired: reCalculationRequired,
            };

            const recordId = getWhitePaperReportRecordId(currentReportId.toString(), currentVersionId);
            await deleteRecord(DB_WHITEPAPERREPORT_NAME, storeName, recordId);

            const cacheRecord = adaptWhitePaperReportToCacheRecord(
                currentReportId.toString(),
                updatedReport,
                enterprise?.enterpriseId,
                currentVersionId
            );
            await insertRecord(DB_WHITEPAPERREPORT_NAME, storeName, cacheRecord);

            setCachedWhitePaperReport(updatedReport);

            return updatedReport;
        } catch (error) {
            appInsights?.trackException({
                exception: error as Error,
                properties: {
                    component: 'useWhitePaperReportCache',
                    operation: 'updateCachedReportWithChanges',
                    reportId: currentReportId.toString(),
                },
                severityLevel: 3,
            });
            throw error;
        }
    };

    const loadWhitePaperReportDataCoordinated = useCallback(
        async (isFormsLoading: boolean): Promise<IWhitePaperReport | null> => {
            return loadWhitePaperReportData(undefined, false, isFormsLoading);
        },
        [loadWhitePaperReportData]
    );

    useEffect(() => {
        if (
            reportId &&
            hasLoadedRef.current !== reportId &&
            storeName &&
            userClaims &&
            enterprise &&
            availableVersions !== undefined &&
            !shouldWaitForCalculationState
        ) {
            hasLoadedRef.current = reportId;

            loadWhitePaperReportData(undefined, false, false);
        }
    }, [reportId, storeName, userClaims, enterprise, availableVersions, shouldWaitForCalculationState, loadWhitePaperReportData]);

    useEffect(() => {
        if (wasExecutingRef.current && !isCurrentlyExecuting && storeName && userClaims && enterprise) {
            loadWhitePaperReportData(undefined, true, false);
        }

        wasExecutingRef.current = isCurrentlyExecuting;
    }, [isCurrentlyExecuting, loadWhitePaperReportData, storeName, userClaims, enterprise, reportId]);

    return {
        cachedWhitePaperReport,
        loadWhitePaperReportData,
        loadWhitePaperReportDataCoordinated,
        updateCachedReportWithChanges,
        isLoading: isFetchingWhitePaperReport || isCurrentlyExecuting,
        availableVersions,
        latestCalculatedVersionId,
    };
};
